import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PolicyActionPanel = ({ pendingActions, onSubmitAction, onViewAuditTrail }) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionType, setActionType] = useState('');
  const [rationale, setRationale] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const actionTypes = [
    { value: 'label', label: 'Label Content', description: 'Add warning or context label' },
    { value: 'deprioritize', label: 'Deprioritize', description: 'Reduce content visibility' },
    { value: 'remove', label: 'Remove Content', description: 'Complete content removal' },
    { value: 'demonetize', label: 'Demonetize', description: 'Remove monetization eligibility' }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-error bg-error/10 border-error/20';
      case 'high': return 'text-warning bg-warning/10 border-warning/20';
      case 'medium': return 'text-primary bg-primary/10 border-primary/20';
      case 'low': return 'text-muted-foreground bg-muted/50 border-border';
      default: return 'text-muted-foreground bg-muted/50 border-border';
    }
  };

  const handleSubmitAction = async () => {
    if (!selectedAction || !actionType || !rationale?.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmitAction({
        actionId: selectedAction?.id,
        type: actionType,
        rationale: rationale?.trim(),
        timestamp: new Date()?.toISOString()
      });
      setSelectedAction(null);
      setActionType('');
      setRationale('');
    } catch (error) {
      console.error('Failed to submit action:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Policy Action Center</h2>
          <p className="text-sm text-muted-foreground">DSA-compliant content moderation actions</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="FileText"
          onClick={onViewAuditTrail}
        >
          Audit Trail
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Actions List */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Pending Actions ({pendingActions?.length})</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {pendingActions?.map((action) => (
              <div
                key={action?.id}
                className={`p-4 border rounded-lg cursor-pointer transition-smooth ${
                  selectedAction?.id === action?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
                onClick={() => setSelectedAction(action)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{action?.contentTitle}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{action?.platform}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(action?.priority)}`}>
                    {action?.priority?.toUpperCase()}
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Flagged: {action?.flaggedDate}</span>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} className="text-muted-foreground" />
                    <span className="text-muted-foreground">{action?.timeRemaining}</span>
                  </div>
                </div>
                {action?.verdictId && (
                  <div className="mt-2 flex items-center space-x-2">
                    <Icon name="Shield" size={14} className="text-primary" />
                    <span className="text-xs text-primary">Linked to Verdict #{action?.verdictId}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Form */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">Submit Action</h3>
          {selectedAction ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <h4 className="font-medium text-foreground mb-2">Selected Content</h4>
                <p className="text-sm text-foreground">{selectedAction?.contentTitle}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Platform: {selectedAction?.platform} • Priority: {selectedAction?.priority}
                </p>
              </div>

              <Select
                label="Action Type"
                description="Select the appropriate moderation action"
                options={actionTypes}
                value={actionType}
                onChange={setActionType}
                required
              />

              <Input
                label="Rationale"
                description="Provide detailed justification for DSA audit compliance"
                type="textarea"
                placeholder="Enter detailed rationale for this action..."
                value={rationale}
                onChange={(e) => setRationale(e?.target?.value)}
                required
                minLength={50}
                className="min-h-24"
              />

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSelectedAction(null);
                    setActionType('');
                    setRationale('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  loading={isSubmitting}
                  disabled={!actionType || !rationale?.trim() || rationale?.length < 50}
                  onClick={handleSubmitAction}
                  iconName="Send"
                  iconPosition="right"
                >
                  Submit Action
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="MousePointer" size={48} className="text-muted-foreground mb-4" />
              <h4 className="text-lg font-medium text-foreground mb-2">Select an Action</h4>
              <p className="text-sm text-muted-foreground">
                Choose a pending action from the list to submit your moderation decision
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Quick Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-error">12</div>
            <div className="text-xs text-muted-foreground">Critical Actions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning">28</div>
            <div className="text-xs text-muted-foreground">High Priority</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">156</div>
            <div className="text-xs text-muted-foreground">Completed Today</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">2.4h</div>
            <div className="text-xs text-muted-foreground">Avg Response Time</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyActionPanel;